import { endpoints } from "@/configs/Settings";
import { httpPost, type HttpError } from "src-next/shared/api/httpClient";

import {
  extractGraphApiErrorMessage,
  extractResumableUploadHandle,
  extractResumableUploadSessionId,
  resolveHeaderMediaMimeType,
} from "../utils/whatsappResumableUpload";

const UPLOAD_TIMEOUT_MS = 120000;

type UploadHeaderMediaParams = {
  file: File;
};

function resolveMetaAppId(): string {
  const appId = String(import.meta.env.VITE_META_APP_ID ?? "").trim();
  if (!appId) {
    throw new Error("VITE_META_APP_ID não configurado.");
  }
  return appId;
}

function buildXdsbBaseUrl(): string {
  return String(endpoints.XDSB_domain ?? "").replace(/\/$/, "");
}

function buildCreateUploadSessionUrl(params: {
  appId: string;
  file: File;
  fileType: string;
}): string {
  const url = new URL(
    `${buildXdsbBaseUrl()}/whatsappbusiness/${encodeURIComponent(params.appId)}/uploads`,
  );
  url.searchParams.set("file_name", params.file.name);
  url.searchParams.set("file_length", String(params.file.size));
  url.searchParams.set("file_type", params.fileType);
  return url.toString();
}

/**
 * Passo 2 — POST /{SESSION_ID}
 * SESSION_ID vem completo do passo 1 (pode incluir `?sig=...`).
 * Não aplicar encodeURIComponent no id inteiro.
 */
function buildUploadFileBytesUrl(sessionId: string): string {
  return `${buildXdsbBaseUrl()}/whatsappbusiness/${sessionId}`;
}

async function createUploadSession(params: {
  appId: string;
  file: File;
  fileType: string;
}): Promise<unknown> {
  return httpPost<unknown>(buildCreateUploadSessionUrl(params), undefined, {
    timeout: UPLOAD_TIMEOUT_MS,
    withCredentials: false,
    headers: {
      "Content-Type": undefined as unknown as string,
    },
  });
}

async function uploadFileBytesToSession(params: {
  sessionId: string;
  file: File;
}): Promise<unknown> {
  const fileBytes = await params.file.arrayBuffer();
  return httpPost<unknown, ArrayBuffer>(
    buildUploadFileBytesUrl(params.sessionId),
    fileBytes,
    {
      timeout: UPLOAD_TIMEOUT_MS,
      withCredentials: false,
      headers: {
        file_offset: "0",
        "Content-Type": undefined as unknown as string,
      },
    },
  );
}


export async function uploadWhatsAppTemplateHeaderMedia(
  params: UploadHeaderMediaParams,
): Promise<string> {
  const { file } = params;
  if (!file) {
    throw new Error("Arquivo de amostra é obrigatório para o cabeçalho de mídia.");
  }

  const appId = resolveMetaAppId();
  const fileType = resolveHeaderMediaMimeType(file);

  try {
    const startResponse = await createUploadSession({ appId, file, fileType });

    const handleFromStart = extractResumableUploadHandle(startResponse);
    if (handleFromStart) return handleFromStart;

    const sessionId = extractResumableUploadSessionId(startResponse);
    if (!sessionId) {
      throw new Error("Sessão de upload inválida.");
    }

    const uploadResponse = await uploadFileBytesToSession({ sessionId, file });
    const handle = extractResumableUploadHandle(uploadResponse);
    if (!handle) {
      throw new Error("Handle de mídia inválido.");
    }

    return handle;
  } catch (error) {
    const graphMessage = extractGraphApiErrorMessage((error as HttpError)?.data);
    if (graphMessage) throw new Error(graphMessage);
    throw error;
  }
}
